import { inject } from '@angular/core';
import { Router, type CanActivateFn } from '@angular/router';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { ACCESS_READ_LIMIT_MS } from '../../../core/route-wait';
import { RetouchAccessService } from '../../../core/retouch-access.service';
import { withTimeout } from '../domain/utils/with-timeout.util';

/**
 * «Ретушь» opens whenever retouch is on in the project settings, an empty scope included; off = «Фото».
 * Fail-closed: a failed or timed-out settings read counts as off. Read by the route's id: on a cold entry the
 * project shell selects the project only after the guards ran.
 * `?run=<processId>` (an old run-done link) → the retouched photos in «Фото»: a guard UrlTree, since a component
 * navigation on a cold entry is superseded by the shell's own; `?run=editor` stays on the queue.
 */
export const retouchRouteGuard: CanActivateFn = async route => {
  const pid = route.parent?.paramMap.get('projectId') ?? route.paramMap.get('projectId');
  const facade = inject(ProjectsFacade);
  const access = inject(RetouchAccessService);
  const router = inject(Router);
  if (!pid) return router.createUrlTree(['/projects']);

  const run = route.queryParamMap.get('run');
  if (run && run !== 'editor') {
    return router.createUrlTree(['/projects', pid, 'photos'], { queryParams: { filter: 'retouch-done', run } });
  }

  if (!(facade.retouchScopeLoaded() && facade.currentProjectId() === pid)) void facade.loadRetouchScope(pid);
  const available = await withTimeout(access.load(pid), ACCESS_READ_LIMIT_MS).catch(() => false);
  return available ? true : router.createUrlTree(['/projects', pid, 'photos']);
};
